import React, { useState, useRef, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  Image,
  TouchableOpacity,
  TextInput,
  InteractionManager,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { RootStackParamList } from '../types';
import { Button, Input } from '../components';
import { authService } from '../services/auth';
import { Fonts } from '../constants/fonts';
import { Colors, Radii, Shadows, Spacing } from '../constants';
import { useDialog } from '../contexts';

type Props = NativeStackScreenProps<RootStackParamList, 'Login'>;

export const LoginScreen: React.FC<Props> = ({ navigation }) => {
  const { t } = useTranslation();
  const dialog = useDialog();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const emailInputRef = useRef<TextInput>(null);
  const passwordInputRef = useRef<any>(null);

  // Put the cursor in the email field whenever the screen is shown. `autoFocus`
  // alone is unreliable on native: it fires before the navigation transition
  // ends and the keyboard never appears, so focus after interactions settle.
  useFocusEffect(
    useCallback(() => {
      const task = InteractionManager.runAfterInteractions(() => {
        emailInputRef.current?.focus();
      });
      return () => task.cancel();
    }, [])
  );

  // Email validation helper
  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Clear error when user starts typing
  const handleEmailChange = (text: string) => {
    setEmail(text);
    if (emailError) setEmailError('');
  };

  const handlePasswordChange = (text: string) => {
    setPassword(text);
    if (passwordError) setPasswordError('');
  };

  // Validate on blur (when field loses focus)
  const handleEmailBlur = () => {
    if (!email.trim()) {
      setEmailError(t('common.validation.emailRequired'));
    } else if (!isValidEmail(email.trim())) {
      setEmailError(t('common.validation.emailInvalid'));
    }
  };

  const handlePasswordBlur = () => {
    if (!password) {
      setPasswordError(t('common.validation.passwordRequired'));
    }
  };

  const validateFields = () => {
    let isValid = true;

    // Validate email
    if (!email.trim()) {
      setEmailError(t('common.validation.emailRequired'));
      isValid = false;
    } else if (!isValidEmail(email.trim())) {
      setEmailError(t('common.validation.emailInvalid'));
      isValid = false;
    }

    // Validate password
    if (!password) {
      setPasswordError(t('common.validation.passwordRequired'));
      isValid = false;
    }

    return isValid;
  };

  const handleLogin = async () => {
    // Clear previous errors
    setEmailError('');
    setPasswordError('');

    // Validate fields
    if (!validateFields()) {
      return;
    }

    setLoading(true);
    try {
      await authService.signIn(email.trim(), password);
      // Navigation will be handled by auth state change
    } catch (error: any) {
      // Handle specific auth errors
      const errorMessage = error.message || '';
      
      if (errorMessage.includes('Invalid login credentials')) {
        setEmailError(t('common.validation.invalidCredentials'));
        setPasswordError(t('common.validation.invalidCredentials'));
      } else if (errorMessage.includes('Email not confirmed')) {
        setEmailError(t('common.validation.emailNotConfirmed'));
      } else {
        dialog.alert(t('screens.login.loginFailed'), errorMessage || t('common.errors.unknown'), 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      style={styles.container}
    >
      <ScrollView contentContainerStyle={styles.scrollContent}>
        <View style={styles.formWrapper}>
          <View style={styles.header}>
            <Image
              source={require('../../Logos/clubswap_icon.png')}
              style={styles.logo}
              resizeMode="contain"
            />
            <Text style={styles.title}>{t('screens.login.title')}</Text>
            <Text style={styles.subtitle}>{t('screens.login.subtitle')}</Text>
          </View>

          <View style={styles.form}>
            <Input
              ref={emailInputRef}
              label={t('screens.login.email')}
              icon="mail-outline"
              value={email}
              onChangeText={handleEmailChange}
              onBlur={handleEmailBlur}
              placeholder={t('screens.login.emailPlaceholder')}
              keyboardType="email-address"
              autoCapitalize="none"
              autoComplete="email"
              returnKeyType="next"
              onSubmitEditing={() => passwordInputRef.current?.focus()}
              error={emailError}
            />
            <Input
              label={t('screens.login.password')}
              icon="lock-closed-outline"
              value={password}
              onChangeText={handlePasswordChange}
              onBlur={handlePasswordBlur}
              placeholder={t('screens.login.passwordPlaceholder')}
              secureTextEntry
              autoComplete="password"
              returnKeyType="go"
              onSubmitEditing={handleLogin}
              ref={passwordInputRef}
              error={passwordError}
            />
            <TouchableOpacity
              onPress={() => navigation.navigate('ForgotPassword')}
              style={styles.forgotPasswordLink}
            >
              <Text style={styles.forgotPasswordText}>
                {t('screens.login.forgotPassword')}
              </Text>
            </TouchableOpacity>
            <View style={styles.buttonContainer}>
              <Button
                title={t('common.buttons.login')}
                onPress={handleLogin}
                loading={loading}
                style={styles.loginButton}
              />
              <Button
                title={t('common.buttons.createAccount')}
                onPress={() => navigation.navigate('Register')}
                variant="outline"
                style={styles.secondaryButton}
              />
            </View>
          </View>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.background,
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    padding: 24,
    alignItems: 'center',
  },
  formWrapper: {
    width: '100%',
    ...(Platform.OS === 'web' && {
      maxWidth: 500,
    }),
  },
  header: {
    alignItems: 'center',
    marginBottom: 48,
  },
  logo: {
    width: 96,
    height: 96,
    borderRadius: Radii.panel,
    marginBottom: Spacing.lg,
    ...Shadows.card,
  },
  title: {
    fontSize: 36,
    lineHeight: 43,
    fontFamily: Fonts.display,
    fontWeight: '400',
    color: Colors.textDark,
    marginBottom: 6,
  },
  subtitle: {
    fontSize: 16,
    fontFamily: Fonts.regular,
    color: Colors.textMedium,
  },
  form: {
    width: '100%',
  },
  buttonContainer: {
    width: '100%',
    flexDirection: 'row',
    gap: Spacing.base,
  },
  loginButton: {
    flex: 1,
  },
  secondaryButton: {
    flex: 1,
  },
  forgotPasswordLink: {
    alignSelf: 'flex-end',
    marginBottom: Spacing.base,
  },
  forgotPasswordText: {
    fontSize: 14,
    color: Colors.accentStrong,
    fontFamily: Fonts.medium,
    fontWeight: '500',
  },
});
